"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import { useSession, signOut } from "next-auth/react";
import { beginProductGoogleSignIn } from "/lib/auth/clientProductAuth";
import styles from "./Navbar.module.css";
import NavbarDropdown from "/components/Shufflemaps/Dropdowns/NavbarDropdown/NavbarDropdown";
import ListingDropdown from "/components/Shufflemaps/Dropdowns/ListingDropdown/ListingDropdown";

const UserDropdown = dynamic(
  () => import("/components/Shufflemaps/Dropdowns/UserDropdown/UserDropdown"),
  { ssr: false }
);

// i18n safe getter
const tt = (path, fallback, dict) => {
  try {
    return path
      .split(".")
      .reduce((o, k) => (o && typeof o === "object" ? o[k] : undefined), dict) ?? fallback;
  } catch {
    return fallback;
  }
};

const Navbar = ({
  hideHeader,
  language,
  setLanguage,
  i18n,
  availableLanguages = [],
}) => {
  const { data: session } = useSession();
  const [authPending,setAuthPending]=useState(false);

  // Keep original behavior for offset when header shows/hides
  const navStyle = hideHeader ? { top: 0 } : {};

  // ⬇️ NEW: write the navbar’s live bottom edge to --nav-offset
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    let raf = 0;
    const update = () => {
      const cs = getComputedStyle(el);
      const topPx = parseFloat(cs.top) || 0; // current computed top
      const height = el.getBoundingClientRect().height || 40;
      const bottom = Math.max(0, Math.round(height + Math.max(0, topPx)));
      document.documentElement.style.setProperty("--nav-offset", `${bottom}px`);
    };
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    update(); // initial
    // respond to viewport changes
    window.addEventListener("resize", schedule, { passive: true });
    // respond to class/style changes (e.g., sticky class toggles) and transitions
    const mo = new MutationObserver(schedule);
    mo.observe(el, { attributes: true, attributeFilter: ["class", "style"] });
    el.addEventListener("transitionend", schedule);

    return () => {
      window.removeEventListener("resize", schedule);
      el.removeEventListener("transitionend", schedule);
      mo.disconnect();
    };
  }, [hideHeader]);

  const languageLabels = {
    en: "English",
    es: "Español",
    ru: "Русский",
    ja: "日本語",
    ko: "한국어",
    zh: "中文",
  };

  const signInWithCallback = async (callbackPath) => {
    if(authPending)return;
    setAuthPending(true);
    try{await beginProductGoogleSignIn('shufflemaps',callbackPath);}
    catch(error){console.error('[ShuffleMaps] Sign-in failed',error);setAuthPending(false);}
  };

  const handleAuthClick = () => {
    if (session) {
      const currentPath=`${window.location.pathname}${window.location.search}${window.location.hash}`;
      signOut({callbackUrl:new URL(currentPath,window.location.origin).toString()});
    } else {
      signInWithCallback(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    }
  };

  // Ensure unauthenticated users are routed to sign-in before creating
  const handleCreateClick = (e) => {
    if (!session) {
      e.preventDefault();
      signInWithCallback("/ShuffleMaps/CreateListing");
    }
  };

  // MAIN dropdown: primary browse surfaces
  const leftItems = useMemo(
    () => [
      { label: tt("nav.locations", "Locations", i18n), href: "/ShuffleMaps/Locations" },
      { label: tt("nav.classes", "Classes", i18n), href: "/ShuffleMaps/Classes" },
      { label: tt("nav.spots", "Spots", i18n), href: "/ShuffleMaps/Spots" },
      { label: tt("nav.meetups", "Meetups", i18n), href: "/ShuffleMaps/Meetups" },
      { label: tt("nav.studios", "Studios", i18n), href: "/ShuffleMaps/Studios" },
      { label: tt("nav.events", "Events", i18n), href: "/ShuffleMaps/Events" },
    ],
    [i18n]
  );

  // DISCOVER dropdown: helpers/utilities
  const discoverItems = useMemo(
    () => [
      { label: tt("nav.explore", "Explore Map", i18n), href: "/ShuffleMaps/Explore" },
      { label: tt("nav.eventsCalendar", "Events Calendar", i18n), href: "/ShuffleMaps/EventsCalendar" },
      { label: tt("nav.favorites", "Favorites", i18n), href: "/ShuffleMaps/Favorites" },
    ],
    [i18n]
  );

  return (
    <div className={styles.navbarContent}>
      <nav
        ref={navRef}
        className={`${styles.nav} ${hideHeader ? styles.stickyNav : ""}`}
        style={navStyle}
        aria-label="Main navigation"
      >
        <div className={styles.navLeft} style={{ minWidth: 0, flex: 1 }}>
          {/* Main sections (caret ON) */}
          <NavbarDropdown
            className={styles.navDropdown}
            label={tt("nav.main", "Main", i18n)}
            items={leftItems}
            showCaret={true}
          />
          {/* Discover utilities (caret OFF) */}
          <NavbarDropdown
            className={styles.navDropdown}
            label={tt("nav.discover", "Discover", i18n)}
            items={discoverItems}
            showCaret={false}
          />
          {/* Listings actions */}
          <ListingDropdown
            className={styles.navDropdown}
            onCreateClick={handleCreateClick}
          />
        </div>

        <div className={styles.navRight}>
          <UserDropdown className={styles.profileIcon} />
          <button
            onClick={handleAuthClick}
            className={styles.loginButton}
            disabled={authPending}
            aria-busy={authPending ? "true" : "false"}
          >
            {session
              ? tt("nav.signOut", "Sign out", i18n)
              : authPending
                ? tt("nav.loading", "Loading…", i18n)
                : tt("nav.signIn", "Sign in", i18n)}
          </button>
          <div className={styles.languageWrapper}>
            <select
              className={styles.languageSelect}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              title={tt("nav.selectLanguage", "Select Language", i18n)}
              aria-label={tt("nav.selectLanguage", "Select Language", i18n)}
            >
              {availableLanguages.map((code) => (
                <option key={code} value={code}>
                  {languageLabels[code] || code.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
